const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';

const defaultHeaders = {
  'Content-Type': 'application/json'
};

const getClientApiKey = () => {
  if (typeof process !== 'undefined' && process.env.REACT_APP_API_KEY) {
    return process.env.REACT_APP_API_KEY;
  }
  if (typeof window !== 'undefined') {
    return window.localStorage?.getItem('marketreachApiKey') || null;
  }
  return null;
};

const getToken = () => {
  if (typeof window !== 'undefined') {
    return window.localStorage?.getItem('marketreachToken') || null;
  }
  return null;
};

const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  const data = isJson ? await response.json() : null;

  if (!response.ok) {
    const error = new Error(data?.error || 'Request failed');
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
};

const request = async (endpoint, options = {}) => {
  const apiKey = getClientApiKey();

  const config = {
    headers: { ...defaultHeaders, ...(options.headers || {}) },
    ...options
  };

  const token = getToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  if (apiKey) {
    config.headers['x-api-key'] = apiKey;
  }

  if (config.body && typeof config.body !== 'string') {
    config.body = JSON.stringify(config.body);
  }

  let url = `${API_BASE_URL}${endpoint}`;
  if (apiKey) {
    url += (endpoint.includes('?') ? '&' : '?') + `apiKey=${encodeURIComponent(apiKey)}`;
  }

  const response = await fetch(url, config);
  return handleResponse(response);
};

export const api = {
  status: () => request('/status'),
  settings: {
    get: () => request('/settings'),
    update: (payload) => request('/settings', { method: 'PUT', body: payload }),
    setAutomation: (mode, confirm = false) => request('/settings/automation', {
      method: 'POST',
      body: { mode, confirm }
    })
  },
  contacts: {
    list: (params = {}) => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', params.page);
      if (params.limit) searchParams.set('limit', params.limit);
      if (params.filter) searchParams.set('filter', params.filter);
      if (params.search) searchParams.set('search', params.search);
      if (params.tag) searchParams.set('tag', params.tag);
      const query = searchParams.toString();
      return request(`/contacts${query ? `?${query}` : ''}`);
    },
    create: (payload) => request('/contacts', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/contacts/${id}`, { method: 'PUT', body: payload }),
    remove: (id) => request(`/contacts/${id}`, { method: 'DELETE' }),
    removeAll: (confirm) => request('/contacts', { method: 'DELETE', body: { confirm } }),
    removeBySource: (source) => request(`/contacts/source?source=${encodeURIComponent(source)}`, { method: 'DELETE' }),
    bulkImport: (payload) => request('/contacts/bulk', { method: 'POST', body: { contacts: payload } })
  },
  templates: {
    list: () => request('/templates'),
    create: (payload) => request('/templates', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/templates/${id}`, { method: 'PUT', body: payload }),
    remove: (id) => request(`/templates/${id}`, { method: 'DELETE' })
  },
  campaigns: {
    list: () => request('/campaigns'),
    create: (payload) => request('/campaigns', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/campaigns/${id}`, { method: 'PUT', body: payload }),
    remove: (id) => request(`/campaigns/${id}`, { method: 'DELETE' }),
    progress: (id) => request(`/campaigns/${id}/progress`),
    testEmail: (payload) => request('/campaigns/test-email', { method: 'POST', body: payload }),
    sendTestEmail: (templateId) => request('/campaigns/test-email', { method: 'POST', body: { templateId } }),
    testSms: (templateId, contactId, phone) =>
      request('/campaigns/test-sms', { method: 'POST', body: { templateId, contactId, phone } }),
    sendSingleEmail: (templateId, contactId, email) => request('/campaigns/send-single-email', { method: 'POST', body: { templateId, contactId, email } }),
    sendSingleSms: (templateId, contactId, phone) =>
      request('/campaigns/send-single-sms', { method: 'POST', body: { templateId, contactId, phone } })
  },
  unsubscribe: ({ id, email } = {}) => request('/unsubscribe', {
    method: 'POST',
    body: { id, email }
  }),
  suppression: {
    list: () => request('/suppression'),
    create: (payload) => request('/suppression', { method: 'POST', body: payload }),
    remove: (id) => request(`/suppression/${id}`, { method: 'DELETE' })
  },
  analytics: {
    getEmailAnalytics: () => request('/analytics/emails')
  },
  smsInbox: {
    list: (params = {}) => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', params.page);
      if (params.limit) searchParams.set('limit', params.limit);
      const query = searchParams.toString();
      return request(`/sms-inbox${query ? `?${query}` : ''}`);
    }
  },
  auth: {
    login: (email, password) =>
      request('/auth/login', { method: 'POST', body: { email, password } }),
    acceptInvite: (token, password) =>
      request('/auth/accept-invite', { method: 'POST', body: { token, password } }),
    me: () => request('/auth/me')
  },
  invites: {
    validate: (token) => request(`/invites/validate/${encodeURIComponent(token)}`),
    create: (email) => request('/invites', { method: 'POST', body: { email } }),
    list: (pendingOnly = false) =>
      request(`/invites${pendingOnly ? '?pending=true' : ''}`)
  }
};

export default api;
